<template>
  <div class="relative">
    <div
      class="flex items-center w-full border-b-2 transition-colors"
      :class="focused ? 'border-black' : 'border-gray-300'"
    >
      <!-- Country selector — тільки прапор + стрілка -->
      <button
        type="button"
        class="flex items-center gap-1 pr-3 py-2 flex-shrink-0 hover:opacity-75 transition-opacity"
        @click="toggleDropdown"
      >
        <FlagIcon :iso-code="selected.isoCode" :width="22" />
        <svg class="w-3 h-3 text-gray-400 transition-transform" :class="open ? 'rotate-180' : ''" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </button>

      <!-- + префікс -->
      <span class="font-pt-serif text-lg text-gray-800 pr-0.5 flex-shrink-0">+</span>

      <!-- Поле: код країни + номер разом -->
      <input
        ref="inputRef"
        :value="displayDigits"
        type="tel"
        inputmode="numeric"
        :placeholder="placeholderDigits"
        class="flex-1 py-2 font-pt-serif text-lg bg-transparent outline-none min-w-0"
        @focus="focused = true"
        @blur="focused = false"
        @input="onInput"
      />
    </div>

    <!-- Dropdown -->
    <div
      v-if="open"
      class="absolute top-full left-0 z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden"
    >
      <div class="p-2">
        <input
          v-model="search"
          type="text"
          :placeholder="t('form.searchCountry')"
          class="w-full px-3 py-1.5 text-sm font-montserrat border border-gray-200 rounded outline-none focus:border-gray-400"
          @click.stop
        />
      </div>
      <ul class="max-h-52 overflow-y-auto">
        <li
          v-for="code in filteredCodes"
          :key="code.isoCode"
          class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 font-montserrat text-sm"
          :class="code.isoCode === selected.isoCode ? 'bg-gray-100 font-medium' : ''"
          @click="select(code)"
        >
          <FlagIcon :iso-code="code.isoCode" :width="20" />
          <span class="flex-1 truncate">{{ code.label }}</span>
          <span class="text-gray-400">+{{ code.phoneCode }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { PHONE_CODES, findByIso } from '~/constants/phone-codes'
import type { PhoneCode } from '~/constants/phone-codes'

const props = defineProps<{ modelValue?: string }>()
const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>()
const { t } = useI18n()

const selected = ref<PhoneCode>(PHONE_CODES[0]) // UA за замовчуванням
// Повні цифри номера включно з кодом країни (без +)
const allDigits = ref(selected.value.phoneCode)
const open = ref(false)
const search = ref('')
const focused = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

// Те що показується в полі вводу — всі цифри (код + номер)
const displayDigits = computed(() => allDigits.value)

// Плейсхолдер = код країни (щоб показати яких цифр бракує)
const placeholderDigits = computed(() => selected.value.phoneCode)

const filteredCodes = computed(() => {
  const q = search.value.toLowerCase()
  if (!q) return PHONE_CODES
  return PHONE_CODES.filter(c => c.label.toLowerCase().includes(q) || c.phoneCode.includes(q))
})

function select(code: PhoneCode) {
  selected.value = code
  allDigits.value = code.phoneCode
  open.value = false
  search.value = ''
  emit('update:modelValue', `+${allDigits.value}`)
  inputRef.value?.focus()
}

function toggleDropdown() {
  open.value = !open.value
  if (open.value) search.value = ''
}

function onInput(e: Event) {
  const raw = (e.target as HTMLInputElement).value
  const digits = raw.replace(/\D/g, '')
  allDigits.value = digits

  // Автодетект країни по префіксу (від довших до коротших)
  const sorted = [...PHONE_CODES].sort((a, b) => b.phoneCode.length - a.phoneCode.length)
  const match = sorted.find(c => digits.startsWith(c.phoneCode))
  if (match) selected.value = match

  emit('update:modelValue', digits ? `+${digits}` : '')
}

function closeOnOutside(e: MouseEvent) {
  if (!(e.target as HTMLElement).closest('.relative')) open.value = false
}

async function detectCountry() {
  try {
    const res = await fetch('/api/geo/country', { signal: AbortSignal.timeout(3000) })
    if (!res.ok) return
    const data = await res.json() as { country: string | null }
    if (data.country) {
      const found = findByIso(data.country)
      if (found) {
        selected.value = found
        allDigits.value = found.phoneCode
        emit('update:modelValue', `+${found.phoneCode}`)
      }
    }
  } catch { /* best-effort */ }
}

onMounted(() => {
  window.addEventListener('click', closeOnOutside)
  detectCountry()
})

onUnmounted(() => {
  window.removeEventListener('click', closeOnOutside)
})
</script>
